"use client"
import { ComponentConfig } from "@measured/puck"
import Image from "next/image"
import { useEffect, useState } from "react"
import { ADMIN_API_CONFIG } from "../../../../../../constants/api-configs"
import { DefaultImage } from "../../../../../../lib/external-components"
import { generateResponsiveCSS } from "../../lib/helper"

export type PhotoLibraryProps = {
  items: { id?: number; image?: string; url: string; title: string }[]
  height?: number
}

export const RenderConfig: ComponentConfig<PhotoLibraryProps> = {
  render: ({ items }) => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [currentImage, setCurrentImage] = useState("")
    const [showAllImages, setShowAllImages] = useState(false)
    const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768)

    const openImageModal = (image: string) => {
      setCurrentImage(image)
      setIsModalOpen(true)
    }

    const closeImageModal = () => {
      setIsModalOpen(false)
    }

    const id = `gallery-${Math.random().toString(36).substr(2, 9)}`

    // Default styles for desktop
    const desktopStyles = {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gap: "2rem",
    }

    // Styles for mobile
    const mobileStyles = {
      display: "flex",
      flexDirection: "column",
      gap: "1rem",
    }

    // Generate responsive CSS
    const responsiveCSS = generateResponsiveCSS(id, {
      mobile: mobileStyles,
      desktop: desktopStyles,
    })

    // Check if the screen size changes
    useEffect(() => {
      const handleResize = () => {
        setIsDesktop(window.innerWidth >= 768)
      }

      window.addEventListener("resize", handleResize)
      return () => window.removeEventListener("resize", handleResize)
    }, [])

    // Determine the items to render based on the showAllImages state or if it's desktop mode
    const itemsToRender = isDesktop || showAllImages ? items : items.slice(0, 6)

    return (
      <>
        {responsiveCSS}
        <div className={`${id} container`}>
          {itemsToRender.map(({ id, image }, index) => (
            <div key={index} className="relative group">
              <Image
                src={image ? `${ADMIN_API_CONFIG.API_URL}${image}` : DefaultImage}
                alt={`gallery item ${id}`}
                className={`${id} image w-full h-[333px] object-cover shadow-md cursor-pointer transition-transform transform hover:scale-105 `}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                onClick={() => image && openImageModal(image)}
              />
            </div>
          ))}

          {!isDesktop && !showAllImages && items.length > 6 && (
            <button
              className="mt-4 px-4 py-2 bg-white text-green border border-green font-semibold rounded-xl md:hidden"
              onClick={() => setShowAllImages(true)}
            >
              Xem thêm
            </button>
          )}

          {isModalOpen && (
            <div
              className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-50"
              onClick={closeImageModal}
            >
              <Image
                src={`${ADMIN_API_CONFIG.API_URL}${currentImage}`}
                alt="Large View"
                className="max-w-full max-h-full "
                style={{ width: "800px", height: "600px", objectFit: "contain" }}
              />

              <span className="absolute top-4 right-4 text-white text-3xl cursor-pointer" onClick={closeImageModal}>
                &times;
              </span>
            </div>
          )}
        </div>
      </>
    )
  },
}
